/**
 * useDischargeQueue Hook
 * Manages discharge power selection with backend queue persistence.
 * Powers survive page refreshes by syncing to /oath/discharge/queue endpoints.
 *
 * Strategy: Optimistic UI — local state updates immediately, API calls
 * happen in the background. Failures show a toast but do not roll back.
 *
 * @param {Object} [options]
 * @param {number} [options.agencyId] - Agency ID passed to queue add operations
 * @param {number} [options.reportId] - If set, scopes all queue operations to this draft
 */

import { useState, useCallback, useRef, useEffect } from 'react';
import { toast } from 'react-toastify';
import {
  getDischargeQueue,
  addToQueue,
  addBulkToQueue,
  removeFromQueue,
  clearQueue,
  fetchEligiblePowers,
} from '../services/oathDischargeService';

export const useDischargeQueue = ({ agencyId, reportId } = {}) => {
  const [selectedPowerNos, setSelectedPowerNos] = useState(new Set());
  const [isLoadingQueue, setIsLoadingQueue] = useState(true);

  // Power metadata for display (defendant name, bond amount, status)
  // Populated from queue API items and from typeahead suggestion selections
  const [powerDetailsMap, setPowerDetailsMap] = useState(new Map());

  // Ref mirror of powerDetailsMap for synchronous access in fire-and-forget callbacks
  const powerDetailsMapRef = useRef(new Map());

  // Keep ref in sync with state so queue load, enrichment, and remove all
  // propagate to the ref before the next user action reads it.
  useEffect(() => {
    powerDetailsMapRef.current = powerDetailsMap;
  }, [powerDetailsMap]);

  // Ref to track the "last known" set for diffing in handleSelectionChange.
  // Using a ref (not state) for synchronous read access without stale closures.
  const prevSetRef = useRef(new Set());

  // Track whether initial load is done to avoid syncing during hydration
  const initialLoadDone = useRef(false);

  // Powers currently being saved to the queue (API in flight)
  const [savingPowerNos, setSavingPowerNos] = useState(new Set());

  // ============================================
  // INITIAL QUEUE FETCH (re-runs when reportId changes)
  // ============================================

  useEffect(() => {
    let cancelled = false;
    initialLoadDone.current = false;

    const loadQueue = async () => {
      setIsLoadingQueue(true);

      try {
        const result = await getDischargeQueue({ reportId });
        if (cancelled) return;

        if (result.success && result.data) {
          const items = result.data.items || [];
          const newSet = new Set(items.map((item) => item.powerNumber));
          setSelectedPowerNos(newSet);
          prevSetRef.current = newSet;

          // Preserve power metadata from queue items for display
          const detailsMap = new Map();
          items.forEach((item) => {
            // Build serverValidation from queue response fields when available.
            // isValid=false means the power's status changed since it was queued.
            let serverValidation;
            if (item.isValid === false) {
              serverValidation = {
                status: 'blocked',
                reason: item.validationError || 'This power cannot be discharged.',
              };
            } else if (item.requiresProof != null || item.proofReason) {
              serverValidation = item.requiresProof
                ? { status: 'needsProof', reason: item.proofReason || 'Proof required' }
                : { status: 'ready' };
            }
            detailsMap.set(item.powerNumber, {
              defendantName: item.defendantName,
              bondAmount: item.liability,
              status: item.status,
              statusName: item.statusName || null,
              type: item.type,
              hasProofUploaded: item.hasProofUploaded || false,
              ...(serverValidation && { serverValidation }),
            });
          });
          setPowerDetailsMap(detailsMap);

          const invalidCount = result.data.invalidCount || 0;
          if (invalidCount > 0) {
            toast.warning(
              `${invalidCount} queued power${invalidCount === 1 ? ' is' : 's are'} no longer valid for discharge. Review the flagged rows.`,
            );
          }

          // Enrich queue items with fields the eligible powers API provides
          // that the queue may not have (or may have stored as null).
          // Use ?? to avoid overwriting queue data with undefined/null.
          if (items.length > 0 && agencyId) {
            try {
              const eligible = await fetchEligiblePowers({ agencyId });
              if (!cancelled && eligible.success && eligible.data?.powers) {
                const lookup = new Map(eligible.data.powers.map((p) => [p.powerNo, p]));
                setPowerDetailsMap((prev) => {
                  const enriched = new Map(prev);
                  let changed = false;
                  for (const item of items) {
                    const power = lookup.get(item.powerNumber);
                    if (power) {
                      const existing = prev.get(item.powerNumber) || {};
                      enriched.set(item.powerNumber, {
                        ...existing,
                        defendantName: power.defendantName ?? existing.defendantName,
                        bondAmount: power.bondAmount ?? existing.bondAmount,
                        status: power.status || existing.status,
                        state: power.state ?? existing.state,
                        isForfeiture: power.isForfeiture ?? existing.isForfeiture,
                        batchClosed: power.batchClosed ?? existing.batchClosed,
                      });
                      changed = true;
                    }
                  }
                  return changed ? enriched : prev;
                });
              }
            } catch (err) {
              console.warn('Could not enrich queue items with power details:', err);
            }
          }
        } else {
          console.warn('Queue load failed:', result.error);
          toast.error('Could not restore your saved power selections.');
        }
      } catch (err) {
        if (cancelled) return;
        console.error('Queue load error:', err);
        toast.error('Could not restore your saved power selections.');
      } finally {
        if (!cancelled) {
          setIsLoadingQueue(false);
          initialLoadDone.current = true;
        }
      }
    };

    loadQueue();

    return () => { cancelled = true; };
  }, [reportId, agencyId]);

  // ============================================
  // SELECTION CHANGE (diff-based sync)
  // ============================================

  const handleSelectionChange = useCallback((newSet) => {
    // Update local state immediately (optimistic)
    setSelectedPowerNos(newSet);

    // Skip sync if initial load hasn't completed
    if (!initialLoadDone.current) {
      prevSetRef.current = newSet;
      return;
    }

    const oldSet = prevSetRef.current;

    // Compute diff
    const added = [];
    const removed = [];

    for (const powerNo of newSet) {
      if (!oldSet.has(powerNo)) added.push(powerNo);
    }
    for (const powerNo of oldSet) {
      if (!newSet.has(powerNo)) removed.push(powerNo);
    }

    // Update ref for next diff
    prevSetRef.current = newSet;

    // Fire-and-forget: sync additions (include metadata so queue persists display fields)
    if (added.length > 0) {
      // Mark powers as saving
      setSavingPowerNos((prev) => {
        const next = new Set(prev);
        for (const no of added) next.add(no);
        return next;
      });

      const detailsSnap = powerDetailsMapRef.current;
      (async () => {
        try {
          let result;
          if (added.length === 1) {
            const d = detailsSnap.get(added[0]);
            result = await addToQueue(added[0], {
              agencyId,
              reportId,
              defendantName: d?.defendantName,
              liability: d?.bondAmount,
              status: d?.status,
              type: d?.type,
            });
          } else {
            const powersWithMeta = added.map((powerNo) => {
              const d = detailsSnap.get(powerNo);
              return {
                powerNumber: powerNo,
                defendantName: d?.defendantName,
                bondAmount: d?.bondAmount,
                status: d?.status,
                type: d?.type,
              };
            });
            result = await addBulkToQueue(powersWithMeta, { agencyId, reportId });
          }
          if (!result.success) {
            // Backend add failed — roll back the optimistic local add so no
            // unverified power lingers in the queue.
            console.warn('Queue add sync failed:', result.error);
            setSelectedPowerNos((prev) => {
              const next = new Set(prev);
              for (const no of added) next.delete(no);
              prevSetRef.current = next;
              return next;
            });
            // The bulk endpoint returns per-power validations[] even when the
            // top-level success is false — surface each power's own reason
            // rather than the generic "No valid powers to add" message.
            const failed = (result.data?.validations || []).filter((v) => v.isValid === false);
            if (failed.length > 0) {
              failed.forEach((v) =>
                toast.error(`${v.powerNo}: ${v.errorMessage || v.validationErrors?.[0] || 'Cannot be discharged.'}`),
              );
            } else {
              toast.error(result.error || 'Could not add power to discharge queue.');
            }
            return;
          }

          // Enrich powerDetailsMap with server validation data from queue add response.
          // Single add returns { validation: {...}, hasProofUploaded }, bulk returns { validations: [...] }.
          const validations = result.data?.validation
            ? [result.data.validation]
            : result.data?.validations || [];
          // hasProofUploaded lives at the response level for single add
          const responseHasProof = result.data?.hasProofUploaded || false;

          if (validations.length > 0) {
            setPowerDetailsMap((prev) => {
              const enriched = new Map(prev);
              let changed = false;
              for (const v of validations) {
                if (!v.powerNo) continue;
                const existing = prev.get(v.powerNo) || {};
                // Map server validation result to the same shape as validatePowerForDischarge
                let serverValidation;
                if (v.isValid === false) {
                  serverValidation = { status: 'blocked', reason: v.errorMessage || v.validationErrors?.[0] || 'Invalid' };
                } else if (v.requiresProof) {
                  serverValidation = { status: 'needsProof', reason: v.proofReason || 'Proof required' };
                } else if (v.isValid) {
                  serverValidation = { status: 'ready' };
                }
                enriched.set(v.powerNo, {
                  ...existing,
                  defendantName: v.defendantName ?? existing.defendantName,
                  bondAmount: v.bondAmount ?? existing.bondAmount,
                  status: v.powerStatus || existing.status,
                  state: v.state ?? existing.state,
                  hasProofUploaded: v.hasProofUploaded ?? responseHasProof ?? existing.hasProofUploaded,
                  ...(serverValidation && { serverValidation }),
                });
                changed = true;
              }
              return changed ? enriched : prev;
            });

            // Handle invalid powers rejected by server validation
            const invalid = validations.filter((v) => v.isValid === false);
            if (invalid.length > 0) {
              const invalidNos = invalid.map((v) => v.powerNo);
              setSelectedPowerNos((prev) => {
                const next = new Set(prev);
                for (const no of invalidNos) {
                  next.delete(no);
                }
                prevSetRef.current = next;
                return next;
              });
              toast.error(
                `${invalid.length} power(s) could not be added: ${invalidNos.join(', ')}`,
              );
            }
          }
        } catch (err) {
          // Network/exception — roll back the optimistic local add.
          console.warn('Queue add sync error:', err);
          setSelectedPowerNos((prev) => {
            const next = new Set(prev);
            for (const no of added) next.delete(no);
            prevSetRef.current = next;
            return next;
          });
          toast.error('Network error — could not add power to discharge queue.');
        } finally {
          setSavingPowerNos((prev) => {
            const next = new Set(prev);
            for (const no of added) next.delete(no);
            return next;
          });
        }
      })();
    }

    // Fire-and-forget: sync removals
    if (removed.length > 0) {
      (async () => {
        try {
          if (newSet.size === 0) {
            // Entire set cleared — use bulk clear
            const result = await clearQueue({ reportId });
            if (!result.success) {
              console.warn('Queue clear sync failed:', result.error);
            }
          } else {
            // Remove individually
            const results = await Promise.allSettled(
              removed.map((powerNo) => removeFromQueue(powerNo, { reportId })),
            );
            const failed = results.filter(
              (r) => r.status === 'rejected' || (r.status === 'fulfilled' && !r.value.success),
            );
            if (failed.length > 0) {
              console.warn(`Queue remove sync: ${failed.length}/${removed.length} failed`);
            }
          }
        } catch (err) {
          console.warn('Queue remove sync error:', err);
        }
      })();
    }
  }, [agencyId, reportId]);

  // ============================================
  // EXPLICIT REMOVE (from Review tab)
  // ============================================

  const handleRemovePower = useCallback((powerNo) => {
    setSelectedPowerNos((prev) => {
      const next = new Set(prev);
      next.delete(powerNo);
      prevSetRef.current = next;
      return next;
    });

    setPowerDetailsMap((prev) => {
      const next = new Map(prev);
      next.delete(powerNo);
      return next;
    });

    // Fire-and-forget
    removeFromQueue(powerNo, { reportId }).catch((err) => {
      console.warn('Queue remove sync error:', err);
    });
  }, [reportId]);

  // ============================================
  // CLEAR ALL
  // ============================================

  const handleClearAll = useCallback(() => {
    setSelectedPowerNos(new Set());
    prevSetRef.current = new Set();
    setPowerDetailsMap(new Map());

    // Fire-and-forget
    clearQueue({ reportId }).catch((err) => {
      console.warn('Queue clear sync error:', err);
    });
  }, [reportId]);

  // ============================================
  // RELOAD QUEUE (for external triggers, e.g. after draft creation)
  // ============================================

  const reloadQueue = useCallback(async () => {
    setIsLoadingQueue(true);
    try {
      const result = await getDischargeQueue({ reportId });
      if (result.success && result.data) {
        const items = result.data.items || [];
        const newSet = new Set(items.map((item) => item.powerNumber));
        setSelectedPowerNos(newSet);
        prevSetRef.current = newSet;

        const detailsMap = new Map();
        items.forEach((item) => {
          let serverValidation;
          if (item.isValid === false) {
            serverValidation = {
              status: 'blocked',
              reason: item.validationError || 'This power cannot be discharged.',
            };
          } else if (item.requiresProof != null || item.proofReason) {
            serverValidation = item.requiresProof
              ? { status: 'needsProof', reason: item.proofReason || 'Proof required' }
              : { status: 'ready' };
          }
          detailsMap.set(item.powerNumber, {
            defendantName: item.defendantName,
            bondAmount: item.liability,
            status: item.status,
            statusName: item.statusName || null,
            type: item.type,
            hasProofUploaded: item.hasProofUploaded || false,
            ...(serverValidation && { serverValidation }),
          });
        });
        setPowerDetailsMap(detailsMap);

        // Enrich with eligible powers API fields that the queue may not have.
        if (items.length > 0 && agencyId) {
          try {
            const eligible = await fetchEligiblePowers({ agencyId });
            if (eligible.success && eligible.data?.powers) {
              const lookup = new Map(eligible.data.powers.map((p) => [p.powerNo, p]));
              setPowerDetailsMap((prev) => {
                const enriched = new Map(prev);
                let changed = false;
                for (const item of items) {
                  const power = lookup.get(item.powerNumber);
                  if (power) {
                    const existing = prev.get(item.powerNumber) || {};
                    enriched.set(item.powerNumber, {
                      ...existing,
                      defendantName: power.defendantName ?? existing.defendantName,
                      bondAmount: power.bondAmount ?? existing.bondAmount,
                      status: power.status || existing.status,
                      state: power.state ?? existing.state,
                      isForfeiture: power.isForfeiture ?? existing.isForfeiture,
                      batchClosed: power.batchClosed ?? existing.batchClosed,
                    });
                    changed = true;
                  }
                }
                return changed ? enriched : prev;
              });
            }
          } catch (err) {
            console.warn('Could not enrich queue items with power details:', err);
          }
        }
      }
    } finally {
      setIsLoadingQueue(false);
    }
  }, [reportId, agencyId]);

  // Called by PowerNumberEntry when a suggestion is selected, to store metadata
  const updatePowerDetails = useCallback((powerNo, details) => {
    // Update ref synchronously so handleSelectionChange can read it immediately
    const next = new Map(powerDetailsMapRef.current);
    next.set(powerNo, details);
    powerDetailsMapRef.current = next;
    setPowerDetailsMap(next);
  }, []);

  // Pessimistic add for tokens the user typed/pasted that didn't match a
  // pre-validated suggestion. We call the backend first and only add to the
  // queue once the server confirms the power is valid — so a random string
  // never flashes in the UI or gets persisted server-side.
  // Returns { added: string[], invalid: [{ powerNo, reason }] }.
  const addUnverifiedPowers = useCallback(async (tokens) => {
    // Read the live set via ref so back-to-back calls (rapid typing) don't
    // double-submit tokens that were just added by a pending call.
    const currentSet = prevSetRef.current;
    const unique = Array.from(new Set(tokens)).filter((t) => t && !currentSet.has(t));
    if (unique.length === 0) return { added: [], invalid: [] };

    setSavingPowerNos((prev) => {
      const next = new Set(prev);
      for (const t of unique) next.add(t);
      return next;
    });

    try {
      let result;
      if (unique.length === 1) {
        result = await addToQueue(unique[0], { agencyId, reportId });
      } else {
        result = await addBulkToQueue(
          unique.map((powerNumber) => ({ powerNumber })),
          { agencyId, reportId },
        );
      }

      // The bulk endpoint returns a per-power validations[] array even when the
      // top-level success is false (e.g. "No valid powers to add to queue.").
      // Prefer those per-power reasons over the generic top-level message.
      // Only when there's no validations[] at all (network/HTTP error) do we
      // fall back to the top-level error for every token.
      const validations = result.data?.validation
        ? [result.data.validation]
        : result.data?.validations || [];

      if (validations.length === 0) {
        return {
          added: [],
          invalid: result.success
            ? []
            : unique.map((powerNo) => ({ powerNo, reason: result.error || 'Could not add power.' })),
        };
      }

      const responseHasProof = result.data?.hasProofUploaded || false;

      const validMap = new Map();
      const invalid = [];
      for (const v of validations) {
        if (!v.powerNo) continue;
        if (v.isValid === false) {
          invalid.push({
            powerNo: v.powerNo,
            reason: v.errorMessage || v.validationErrors?.[0] || 'This power cannot be discharged.',
          });
        } else {
          validMap.set(v.powerNo, v);
        }
      }

      // Tokens that didn't appear in validations at all — treat as unknown error
      for (const t of unique) {
        if (!validMap.has(t) && !invalid.some((i) => i.powerNo === t)) {
          invalid.push({ powerNo: t, reason: 'Power not recognized.' });
        }
      }

      const added = Array.from(validMap.keys());

      if (added.length > 0) {
        setPowerDetailsMap((prev) => {
          const next = new Map(prev);
          for (const [powerNo, v] of validMap) {
            const existing = prev.get(powerNo) || {};
            let serverValidation;
            if (v.requiresProof) {
              serverValidation = { status: 'needsProof', reason: v.proofReason || 'Proof required' };
            } else {
              serverValidation = { status: 'ready' };
            }
            next.set(powerNo, {
              ...existing,
              defendantName: v.defendantName ?? existing.defendantName,
              bondAmount: v.bondAmount ?? existing.bondAmount,
              status: v.powerStatus || existing.status,
              state: v.state ?? existing.state,
              hasProofUploaded: v.hasProofUploaded ?? responseHasProof ?? existing.hasProofUploaded,
              serverValidation,
            });
          }
          powerDetailsMapRef.current = next;
          return next;
        });

        setSelectedPowerNos((prev) => {
          const next = new Set(prev);
          for (const powerNo of added) next.add(powerNo);
          prevSetRef.current = next;
          return next;
        });
      }

      return { added, invalid };
    } catch (err) {
      console.error('addUnverifiedPowers error:', err);
      return {
        added: [],
        invalid: unique.map((powerNo) => ({ powerNo, reason: 'Network error.' })),
      };
    } finally {
      setSavingPowerNos((prev) => {
        const next = new Set(prev);
        for (const t of unique) next.delete(t);
        return next;
      });
    }
  }, [agencyId, reportId]);

  return {
    selectedPowerNos,
    setSelectedPowerNos: handleSelectionChange,
    handleRemovePower,
    handleClearAll,
    isLoadingQueue,
    powerDetailsMap,
    updatePowerDetails,
    reloadQueue,
    savingPowerNos,
    addUnverifiedPowers,
  };
};

export default useDischargeQueue;
